/**
 * Domain Utils - Funções utilitárias para gerenciamento de domínios
 */

/**
 * Retorna a cor do chip de prioridade baseado no valor
 * @param {number} priority - Nível de prioridade (1-5)
 * @returns {string} - Cor do Material-UI
 */
export const getPriorityColor = (priority) => {
    switch (priority) {
        case 1: return 'error';     // Urgente - vermelho (sem dados)
        case 2: return 'warning';   // Alta - laranja (usado recentemente)
        case 3: return 'info';      // Normal - azul (Tier 1)
        case 4: return 'success';   // Baixa - verde (Tier 2)
        case 5: return 'default';   // Mínima - cinza (Tier 3)
        default: return 'default';
    }
};

/**
 * Retorna o label da prioridade
 * @param {number} priority - Nível de prioridade (1-5)
 * @returns {string} - Label descritivo
 */
export const getPriorityLabel = (priority) => {
    switch (priority) {
        case 1: return 'Scraping';  // Precisa fazer scraping (dados estimados)
        case 2: return 'Alta';      // Usado recentemente
        case 3: return 'Normal';    // Tier 1
        case 4: return 'Baixa';     // Tier 2
        case 5: return 'Mínima';    // Tier 3
        default: return 'N/A';
    }
};

/**
 * Determina ordenação padrão (asc/desc) baseada no tipo de coluna
 * @param {string} column - Nome da coluna
 * @returns {string} - 'asc' ou 'desc'
 */
export const getDefaultSortDirection = (column) => {
    const numericColumns = ['firstSeen', 'processedAt', 'articleCount', 'priority', 'estimatedAudience'];
    return numericColumns.includes(column) ? 'desc' : 'asc';
};

/**
 * Formata audiência para exibição (número completo com separadores)
 * @param {number} audience - Número de audiência
 * @returns {string} - Número formatado (ex: "1.200.000", "350.000", "10,6")
 */
export const formatAudience = (audience) => {
    if (!audience) return '-';

    // Arredondar para número inteiro
    const rounded = Math.round(audience);

    // Formatar com separador de milhares (ponto) - padrão brasileiro
    return rounded.toLocaleString('pt-BR');
};

/**
 * Valida se um domínio é válido
 * @param {string} domain - Nome do domínio
 * @returns {boolean}
 */
export const isValidDomain = (domain) => {
    if (!domain) return false;
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
    return domainRegex.test(domain);
};

/**
 * Extrai domínio registrável de uma URL
 * @param {string} url - URL completa
 * @returns {string} - Domínio registrável
 */
export const extractRegistrableDomain = (url) => {
    try {
        const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
        return urlObj.hostname.replace('www.', '');
    } catch {
        return url.replace('www.', '');
    }
};

/**
 * Retorna o label de status do domínio
 * @param {string} status - Status do domínio (pending, processing, completed)
 * @param {string} dataQuality - Qualidade dos dados (actual, partial, estimated)
 * @returns {string} - Label traduzido
 */
export const getStatusLabel = (status, dataQuality) => {
    if (status === 'pending') return 'Pendente';
    if (status === 'processing') return 'Processando';
    if (status === 'completed') {
        if (dataQuality === 'actual') return 'Completo';
        if (dataQuality === 'partial') return 'Parcial';
        return 'Estimado';
    }
    return status;
};

/**
 * Retorna a cor do chip de status
 * @param {string} status - Status do domínio
 * @param {string} dataQuality - Qualidade dos dados
 * @returns {string} - Cor do Material-UI
 */
export const getStatusColor = (status, dataQuality) => {
    if (status === 'pending') return 'warning';
    if (status === 'processing') return 'info';
    if (status === 'completed') {
        if (dataQuality === 'actual') return 'success';
        if (dataQuality === 'partial') return 'primary';
        return 'secondary';
    }
    return 'default';
};
